import { Card, CardContent, Grid, useTheme } from '@mui/joy';
import { isNumber } from 'lodash';
import { useMemo } from 'react';

import { getPercentage } from 'core/utils/number';
import { ITssDashboardFilters, SummaryMetric } from 'reports/components';
import { useSummary } from 'reports/hooks';
import { Size, Variant } from 'theme/enums';

interface IProps {
  filters?: ITssDashboardFilters;
}

export const DashboardSummary = ({ filters }: IProps) => {
  const theme = useTheme();

  const requestPayload = useMemo(
    () => ({
      filters,
    }),
    [filters]
  );

  const { record, isLoading, isFetching } = useSummary(requestPayload);

  const isDataLoading = isLoading || isFetching;

  const calculateAvgCurrentYearDailyCases = (currentYearBoms: number): number => {
    const today = new Date();
    const yearStart = new Date(today.getFullYear(), 0, 1);
    const daysElapsed = Math.ceil((today.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));

    if (!daysElapsed) {
      return 0;
    }

    return Math.round(currentYearBoms / daysElapsed);
  };

  const calculateVariance = (current: number, previous: number): number | undefined => {
    if (previous === 0) {
      return undefined;
    }

    return ((current - previous) / previous) * 100;
  };

  const getVarianceColor = () => {
    if (isNumber(variance) && variance < 0) {
      return theme.palette.danger[500];
    }
    if (isNumber(variance) && variance > 0) {
      return theme.palette.success[500];
    }

    return undefined;
  };

  const previousYearBoms = record?.previousYearBoms ?? 0;
  const currentYearBoms = record?.currentYearBoms ?? 0;
  const bomsLastMonth = record?.lastMonthBoms ?? 0;
  const bomsYesterday = record?.yesterdayBoms ?? 0;
  const previousYearLines = record?.previousYearLines ?? 0;
  const currentYearLines = record?.currentYearLines ?? 0;
  const avgCurrentYearDailyCases = calculateAvgCurrentYearDailyCases(currentYearBoms);

  const variance = calculateVariance(currentYearBoms, previousYearBoms);

  const formattedVariance = getPercentage(variance, {
    format: '0.0%',
    isRawPercentage: true,
  });

  const group1Metrics = [
    { labelKey: 'labelPreviousYearCases', value: previousYearBoms },
    { labelKey: 'labelCurrentYearCases', value: currentYearBoms },
    {
      color: getVarianceColor(),
      labelKey: 'labelVariance',
      value: formattedVariance,
    },
  ];

  const group2Metrics = [
    { labelKey: 'labelCaseLastMonth', value: bomsLastMonth },
    { labelKey: 'labelCasesYesterday', value: bomsYesterday },
    { labelKey: 'labelAverageDailyCases', value: avgCurrentYearDailyCases },
  ];

  const group3Metrics = [
    { labelKey: 'labelPreviousYearLines', value: previousYearLines },
    { labelKey: 'labelCurrentYearLines', value: currentYearLines },
  ];

  const metricGroups = [group1Metrics, group2Metrics, group3Metrics];

  return (
    <Grid container spacing={2}>
      {metricGroups.map((group, index) => (
        <Grid key={index} xs={12} md={4}>
          <Card variant={Variant.OUTLINED} sx={{ boxShadow: Size.SM, height: '100%' }}>
            <CardContent>
              <SummaryMetric metrics={group} isLoading={isDataLoading} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};
